import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, waLink, loadRazorpayScript, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Wallet, Wrench, FileText, LogOut, MessageCircle, CheckCircle2, CalendarClock,
  Image as ImageIcon,
} from "lucide-react";
import FileUploader from "@/components/site/FileUploader";
import MessageThread from "@/components/site/MessageThread";

export default function TenantDashboard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tform, setTform] = useState({ title: "", description: "", category: "plumbing" });
  const [photos, setPhotos] = useState([]);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get("/tenant/dashboard");
    setData(data);
  };
  useEffect(() => {
    load();
    api.get("/payments/config").then(({ data }) => setPaymentsEnabled(!!data.enabled)).catch(() => {});
  }, []);

  const doLogout = async () => { await logout(); navigate("/"); };

  const payRent = async () => {
    setBusy(true);
    try {
      if (!paymentsEnabled) {
        // Demo path — keep legacy stub working
        const { data: receipt } = await api.post("/tenant/pay-rent");
        toast.success(`Rent paid (Demo). Receipt #${receipt.id.slice(0, 8)}`);
        await load();
        return;
      }
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error("Couldn't load payment SDK. Check your connection."); return; }
      const { data: order } = await api.post("/payments/create-order", { amount: data.rent.amount, purpose: "rent" });
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Hi-Tech Realty",
        description: `Rent · ${data.property.title}`,
        prefill: { name: user.name, email: user.email, contact: user.phone || "" },
        theme: { color: "#071A44" },
        handler: async (res) => {
          try {
            const { data: receipt } = await api.post("/payments/verify", res);
            toast.success(`Payment successful. Receipt #${receipt.id.slice(0, 8)}`);
            await load();
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Payment verification failed.");
          }
        },
        modal: { ondismiss: () => toast("Payment cancelled.") },
      };
      // eslint-disable-next-line no-undef
      new window.Razorpay(options).open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start payment.");
    } finally { setBusy(false); }
  };

  const submitTicket = async (e) => {
    e.preventDefault();
    if (!tform.title || !tform.description) { toast.error("Add a title and description."); return; }
    setBusy(true);
    try {
      await api.post("/tenant/tickets", {
        ...tform,
        photos: photos.map((p) => p.url),
      });
      toast.success("Ticket raised. Your property manager has been notified.");
      setTform({ title: "", description: "", category: "plumbing" });
      setPhotos([]);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="container-x py-16 text-brand-navy">Loading portal…</div>;

  return (
    <div data-testid="page-tenant-dashboard" className="container-x py-12">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="eyebrow">Tenant Service Portal</div>
          <h1 className="mt-3 font-display text-3xl md:text-4xl font-bold text-brand-navy">Welcome, {user?.name || data.tenant_name}.</h1>
          <p className="mt-1 text-sm text-brand-slate/70">{data.property.title} · {data.property.address}</p>
        </div>
        <button data-testid="tenant-logout" onClick={doLogout} className="btn-ghost-navy"><LogOut size={14} /> Logout</button>
      </div>

      <div className="mt-10 grid lg:grid-cols-3 gap-6">
        <div className="card-flat p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="eyebrow">Rent</div>
              <div className="mt-2 font-display text-3xl text-brand-navy font-bold">₹{data.rent.amount.toLocaleString("en-IN")} <span className="text-sm font-normal text-brand-slate/60">/ month</span></div>
              <div className="mt-1 text-sm text-brand-slate/70">Due {data.rent.due_date} · Last paid {data.rent.last_paid}</div>
            </div>
            <span className={`text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-2 border ${data.rent.status === "PAID" ? "text-emerald-600 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200"}`}>
              {data.rent.status}
            </span>
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <button data-testid="tenant-pay-rent" onClick={payRent} disabled={busy} className="btn-cyan disabled:opacity-60">
              <Wallet size={14} /> {busy ? "Processing…" : paymentsEnabled ? "Pay Rent via Razorpay" : "Pay Rent (Demo)"}
            </button>
            <a href="#" onClick={(e) => { e.preventDefault(); toast.success("Rent receipt downloaded (demo)."); }} className="btn-ghost-navy" data-testid="tenant-download-receipt">
              <FileText size={14} /> Download Receipt
            </a>
            <a href={waLink(`Hi ${data.property.manager}, I have a quick question about my rental.`, data.property.manager_phone?.replace(/\D/g, ""))} target="_blank" rel="noreferrer" className="btn-whatsapp" data-testid="tenant-message-manager">
              <MessageCircle size={14}/> WhatsApp Manager
            </a>
          </div>
          {!paymentsEnabled && (
            <div className="mt-4 text-[11px] text-brand-slate/60">
              Razorpay test keys not configured. Once admin adds RAZORPAY_KEY_ID and RAZORPAY_KEY_SECRET in backend env, real payments unlock automatically.
            </div>
          )}
        </div>

        <div className="card-flat p-6">
          <div className="eyebrow">Agreement Renewal</div>
          <div className="mt-2 font-display text-2xl text-brand-navy font-bold">{data.agreement.valid_till}</div>
          <p className="mt-1 text-sm text-brand-slate/70">Your rental agreement is up for renewal. Renewal coordination starts 60 days prior.</p>
          <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-brand-cyan font-semibold">
            <CalendarClock size={14} /> Reminder Active
          </div>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <form onSubmit={submitTicket} className="card-flat p-6 lg:col-span-2" data-testid="tenant-ticket-form">
          <div className="flex items-center justify-between">
            <div className="eyebrow">Raise a Maintenance Ticket</div>
            <Wrench size={18} className="text-brand-cyan" />
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <label className="block sm:col-span-2">
              <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-navy">Issue Title</span>
              <input data-testid="ticket-title" required value={tform.title} onChange={(e) => setTform({...tform, title: e.target.value})}
                className="mt-2 w-full border border-brand-border bg-white px-3 py-3 text-sm focus:outline-none focus:border-brand-cyan" placeholder="E.g., Leakage in bathroom" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-navy">Category</span>
              <select data-testid="ticket-category" value={tform.category} onChange={(e) => setTform({...tform, category: e.target.value})}
                className="mt-2 w-full border border-brand-border bg-white px-3 py-3 text-sm focus:outline-none focus:border-brand-cyan">
                {["plumbing", "electrical", "painting", "appliance", "other"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-navy">Priority</span>
              <select className="mt-2 w-full border border-brand-border bg-white px-3 py-3 text-sm focus:outline-none focus:border-brand-cyan">
                {["normal", "urgent"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-navy">Description</span>
              <textarea data-testid="ticket-description" required rows={3} value={tform.description} onChange={(e) => setTform({...tform, description: e.target.value})}
                className="mt-2 w-full border border-brand-border bg-white px-3 py-3 text-sm focus:outline-none focus:border-brand-cyan" placeholder="Describe what's happening." />
            </label>
            <div className="sm:col-span-2">
              <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-navy">Attach Photos (optional)</span>
              <div className="mt-2"><FileUploader testId="ticket-uploader" accept="image/*" onChange={setPhotos} /></div>
            </div>
          </div>
          <button data-testid="ticket-submit" disabled={busy} className="btn-cyan mt-5 disabled:opacity-60">{busy ? "Submitting…" : "Raise Ticket"}</button>
        </form>

        <div className="card-flat p-6">
          <div className="eyebrow">Your Tickets</div>
          {data.tickets.length === 0 ? (
            <p className="mt-3 text-sm text-brand-slate/70">No tickets yet. Raise one any time.</p>
          ) : (
            <ul className="mt-4 space-y-3 max-h-[440px] overflow-y-auto">
              {data.tickets.slice().reverse().map((t) => (
                <li key={t.id} className="border border-brand-border p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-brand-navy text-sm">{t.title}</span>
                    <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-brand-cyan">{t.status}</span>
                  </div>
                  <div className="text-xs text-brand-slate/70 mt-1">{t.category} · {new Date(t.created_at).toLocaleDateString()}</div>
                  <div className="text-xs text-brand-slate/60 mt-1 line-clamp-2">{t.description}</div>
                  {(t.photos || []).length > 0 && (
                    <div className="mt-2 flex gap-2 flex-wrap">
                      {t.photos.map((u, i) => (
                        <a key={i} href={fileUrl(u)} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 border border-brand-border px-2 py-1 text-[10px] text-brand-navy hover:text-brand-cyan">
                          <ImageIcon size={10} /> photo {i + 1}
                        </a>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-5 text-xs text-brand-slate/60 flex items-center gap-2">
            <CheckCircle2 size={14} className="text-brand-cyan" /> Status updates appear here automatically.
          </div>
        </div>
      </div>

      <div className="mt-6">
        <MessageThread currentUserId={user?.id} />
      </div>
    </div>
  );
}
